import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/api";
import IDCard from "@/components/IDCard";
import { fileToDataUrl } from "@/utils/photo";

const inputCls = "w-full px-4 py-3 rounded-xl border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 text-sm";

export default function Membership() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", level: "village",
    district: "", taluka: "", city: "", village: "", address: "", designation: "", photo: null,
  });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/members/register", {
        ...form,
        photo: form.photo || null,
        email: form.email || null,
        taluka: form.taluka || null,
        city: form.city || null,
        village: form.village || null,
        address: form.address || null,
        designation: form.designation || null,
      });
      localStorage.setItem("sp_token", data.token);
      setUser(data.user);
      toast.success(t.register_success);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const levels = [
    { id: "state", label: t.level_state },
    { id: "district", label: t.level_district },
    { id: "block", label: t.level_block },
    { id: "city", label: t.level_city },
    { id: "village", label: t.level_village },
  ];

  return (
    <div data-testid="membership-page">
      <section className="bg-emerald-950 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="membership-heading">{t.membership_title}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {user && user.role === "member" ? (
          <div className="flex flex-col items-center gap-6">
            <IDCard member={user} />
            <div className="flex gap-4">
              <button onClick={() => window.print()} data-testid="id-card-print-btn"
                className="px-6 py-3 rounded-full bg-emerald-900 text-white font-semibold hover:bg-emerald-800 transition-colors">
                {t.print_card}
              </button>
              <Link to="/dashboard" data-testid="goto-dashboard-btn"
                className="px-6 py-3 rounded-full border-2 border-emerald-900 text-emerald-900 font-semibold hover:bg-emerald-50 transition-colors">
                {t.nav_dashboard}
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 bg-white rounded-2xl border border-emerald-100 shadow-sm p-6 sm:p-10">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-emerald-950 mb-8">{t.register_title}</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="membership-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_name} *</label>
                    <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="member-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_mobile} *</label>
                    <input required pattern="\d{10}" maxLength={10} value={form.mobile} onChange={set("mobile")}
                      className={inputCls} data-testid="member-mobile-input" placeholder="10 digits" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_email}</label>
                    <input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="member-email-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_level} *</label>
                    <select value={form.level} onChange={set("level")} className={inputCls} data-testid="member-level-select">
                      {levels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_district} *</label>
                    <input required value={form.district} onChange={set("district")} className={inputCls} data-testid="member-district-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_taluka}</label>
                    <input value={form.taluka} onChange={set("taluka")} className={inputCls} data-testid="member-taluka-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_city}</label>
                    <input value={form.city} onChange={set("city")} className={inputCls} data-testid="member-city-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_village}</label>
                    <input value={form.village} onChange={set("village")} className={inputCls} data-testid="member-village-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_address}</label>
                    <input value={form.address} onChange={set("address")} className={inputCls} data-testid="member-address-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_designation}</label>
                    <input value={form.designation} onChange={set("designation")} className={inputCls} data-testid="member-designation-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_photo}</label>
                  <input type="file" accept="image/*" onChange={handlePhoto} className={inputCls} data-testid="member-photo-input" />
                  {form.photo && <img src={form.photo} alt="preview" className="mt-3 w-20 h-24 object-cover rounded-lg border border-emerald-200" data-testid="member-photo-preview" />}
                </div>
                <button type="submit" disabled={loading} data-testid="membership-submit-btn"
                  className="w-full py-3.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all disabled:opacity-60">
                  {loading ? "..." : t.submit}
                </button>
              </form>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-8 sticky top-24">
                <h3 className="font-heading font-bold text-emerald-950 mb-4">{t.idcard_title}</h3>
                <ul className="text-sm text-slate-600 space-y-2 mb-6">
                  <li>• {t.member_id}: SP-MH-XXXX-XXXX</li>
                  <li>• {t.designation}, {t.level}, {t.district}</li>
                  <li>• {t.print_card}</li>
                </ul>
                <p className="text-sm text-slate-600">
                  {t.nav_login}?{" "}
                  <Link to="/login" className="text-amber-700 font-semibold hover:underline" data-testid="membership-login-link">
                    {t.member_login}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
